import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import LayoutV1 from "@/core/views/designlanguage/v1/LayoutV1.vue";
import LandingPage from "@/app/views/pages/LandingPage.vue";
import Dashboard from "@/app/views/pages/Dashboard.vue";
import Styles from "@/app/views/pages/Styles.vue";
import appRouter from "@/app/router/app-router";
import SystemUsersGrid from "@/core/views/pages/SystemUsers/SystemUsersGrid.vue";
import SystemUserForm from "@/core/views/pages/SystemUsers/SystemUserForm.vue";
import SystemRoleForm from "@/core/views/pages/SystemRoles/SystemRoleForm.vue";
import SystemRolesGrid from "@/core/views/pages/SystemRoles/SystemRolesGrid.vue";
import AuthInfo from "@/core/views/pages/Debug/AuthInfo.vue";
import OidcCallback from "@/core/views/auth/OidcCallback.vue";
import SessionExpired from "@/core/views/auth/SessionExpired.vue";
import AuthenticationError from "@/core/views/auth/AuthenticationError.vue";
import Error404 from "@/core/views/pages/Errors/404.vue";
import TenantsGrid from "@/core/views/pages/SaaS/Tenants/TenantsGrid.vue";
import TenantForm from "@/core/views/pages/SaaS/Tenants/TenantForm.vue";
import HostFeaturesForm from "@/core/views/pages/SaaS/Host/HostFeaturesForm.vue";
import ActivateTenant from "@/core/views/auth/ActivateTenant.vue";
function loadView(view: string) {
  return () => import(/* webpackChunkName: "[request]" */ `@/core/views/pages/${view}.vue`);
}

/**
 * Define Core routes
 * Do not modify. For Application specific routes use @/app/router/app-router.ts
 */
const routes: RouteRecordRaw[] = [
  {
    path: "/oidc-callback", // Needs to match redirectUri (redirect_uri if you use snake case) in you oidcSettings
    name: "oidcCallback",
    component: OidcCallback
  },
  {
    path: "/",
    component: LayoutV1,
    children: [
      {
        path: "/",
        name: "LandingPage",
        component: LandingPage
      },
      {
        path: "/session-expired",
        name: "SessionExpired",
        component: SessionExpired
      },
      {
        path: "/authentication-error",
        name: "AuthenticationError",
        component: AuthenticationError
      },
      {
        path: "/activate-tenant/:organisationId/:tenantId/:accountType?",
        name: "ActivateTenant",
        component: ActivateTenant
      }
    ]
  },
  {
    path: "/",
    component: LayoutV1,
    children: [
      {
        path: "/dashboard",
        name: "Dashboard",
        component: Dashboard,
        meta: {
          title: "Dashboard"
        }
      },
      {
        path: "/styles",
        name: "Styles",
        component: Styles,
        meta: {
          title: "Styles"
        }
      }
    ]
  },
  {
    path: "/systemUsers",
    component: LayoutV1,
    children: [
      {
        path: "",
        name: "SystemUsersGrid",
        component: SystemUsersGrid,
        meta: {
          title: "System Users",
          permission: "AbpIdentity.Users"
        }
      },
      {
        path: "create",
        name: "SystemUserFormCreate",
        component: SystemUserForm,
        meta: {
          title: "System Create User",
          permission: "AbpIdentity.Users.Create"
        }
      },
      {
        path: "edit/:id",
        name: "SystemUserFormEdit",
        component: SystemUserForm,
        meta: {
          title: "Edit System User",
          permission: "AbpIdentity.Users.Update"
        }
      }
    ]
  },
  {
    path: "/systemRoles",
    component: LayoutV1,
    children: [
      {
        path: "",
        name: "SystemRolesGrid",
        component: SystemRolesGrid,
        meta: {
          title: "System Roles",
          permission: "AbpIdentity.Roles"
        }
      },
      {
        path: "create",
        name: "SystemRoleFormCreate",
        component: SystemRoleForm,
        meta: {
          title: "Create System Role",
          permission: "AbpIdentity.Roles.Create"
        }
      },
      {
        path: "edit/:id",
        name: "SystemRoleFormEdit",
        component: SystemRoleForm,
        meta: {
          title: "Edit System Role",
          permission: "AbpIdentity.Roles.Update"
        }
      }
    ]
  },
  ...appRouter,
  {
    path: "/error",
    component: LayoutV1,
    children: [
      {
        path: "401",
        name: "Unauthorized",
        component: loadView("Errors/401"),
        meta: {
          skipLoadingConfig: true
        }
      },
      {
        path: "404",
        name: "NotFound",
        component: loadView("Errors/404"),
        meta: {
          skipLoadingConfig: true
        }
      },
      {
        path: "500",
        name: "InternalServerError",
        component: loadView("Errors/500"),
        meta: {
          skipLoadingConfig: true
        }
      }
    ]
  },
  {
    path: "/saas/hostFeatures",
    component: LayoutV1,
    children: [
      {
        path: "",
        name: "HostFeaturesForm",
        component: HostFeaturesForm,
        meta: {
          title: "Host Features",
          permission: "FeatureManagement.ManageHostFeatures"
        }
      }
    ]
  },
  {
    path: "/saas/tenants",
    component: LayoutV1,
    children: [
      {
        path: "",
        name: "TenantsGrid",
        component: TenantsGrid,
        meta: {
          title: "Tenants",
          permission: "AbpTenantManagement.Tenants"
        }
      },
      {
        path: "create",
        name: "TenantFormCreate",
        component: TenantForm,
        meta: {
          title: "Create Tenant",
          permission: "AbpTenantManagement.Tenants.Create"
        }
      },
      {
        path: "edit/:id",
        name: "TenantFormEdit",
        component: TenantForm,
        meta: {
          title: "Edit Tenant",
          permission: "AbpTenantManagement.Tenants.Update"
        }
      }
    ]
  },
  {
    path: "/debug",
    component: LayoutV1,
    children: [
      {
        path: "auth-info",
        name: "AuthInfo",
        component: AuthInfo,
        meta: {
          title: "AuthInfo",
          feature: "CoatsDigitalCore.DisplayAuthDebug"
        }
      }
    ]
  },
  { path: "/:pathMatch(.*)*", name: "not-found", component: Error404 },
  // if you omit the last `*`, the `/` character in params will be encoded when resolving or pushing
  { path: "/:pathMatch(.*)", name: "bad-not-found", component: Error404 }
];
const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
});
export default router;
